import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMe, useListPeople } from '../../api/generated/identity/identity';
import type { ExecutiveDashboard as ExecutiveDashboardModel, Me } from '../../api/generated/model';
import { useGetExecutiveDashboard } from '../../api/generated/reporting/reporting';
import { useListScheduleCollisions } from '../../api/generated/scheduling/scheduling';
import { Alert } from '../../design/primitives/Alert';
import { formatNumber } from '../../i18n/datetime';
import { formatMoney } from '../billing/money';
import { currentPeriod } from '../payroll/period';
import { Definition, Tile } from './ExecutiveDashboard';
import { PanelSection, panelState } from './PanelSection';
import styles from './dashboard.module.css';

/**
 * Home do Super Admin.
 *
 * O cartao "Em breve" saiu e no lugar dele entrou o que o painel executivo ja
 * devolve — um recorte, com as mesmas definicoes adotadas, e nao numeros soltos.
 * E "Sua proxima acao" deixou de ser uma frase fixa: para quem administra, o que
 * pede acao e a fila de colisoes de grade, que o contrato nunca resolve sozinho
 * porque escolher qual aula se move e decisao de negocio.
 */
export function AdminHome() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const period = useMemo(() => currentPeriod(), []);
  const collisions = useListScheduleCollisions(undefined, { query: { enabled: !!me } });
  const indicators = useGetExecutiveDashboard({ period }, { query: { enabled: !!me } });
  const people = useListPeople(undefined, { query: { enabled: !!me } });

  if (!me) return null;

  const attention = panelState(collisions, (page) => {
    // Colisao resolvida ja nao e fila: ela vira historico na tela de agenda.
    const open = page.items.filter((collision) => collision.resolvedAt === null);
    return open.length > 0 ? open : undefined;
  });
  const roster = panelState(people, (list) => (list.length > 0 ? list : undefined));

  return (
    <>
      <h1 className={styles.title}>{t('dashboard.admin.title')}</h1>

      <PanelSection
        id="admin-next-action"
        title={t('dashboard.nextAction')}
        state={attention}
        empty={t('dashboard.nextActionEmpty')}
      >
        {(open) => (
          <ul className={styles.list}>
            {open.map((collision) => (
              <li key={collision.id} className={styles.listItem}>
                <span>{collision.teacher.displayName}</span>
                {/*
                  Uma chave interpolada, e nao numero colado em substantivo: o
                  JSX entregava ao tradutor "ocorrencias em conflito" sem o
                  numero, e a colisao de uma aula so saia "1 ocorrencias".
                  `schedule.changes.occurrenceCount` e a mesma chave que a
                  agenda e a fila de mudancas ja usam para esta contagem —
                  ela existe nos tres catalogos com as duas formas, e uma
                  chave nova aqui daria dois textos para o mesmo numero.
                */}
                <span className={styles.role}>
                  {t('schedule.changes.occurrenceCount', {
                    count: collision.occurrenceIds.length,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>

      <section className={styles.section} aria-labelledby="admin-indicators">
        <h2 id="admin-indicators" className={styles.sectionTitle}>
          {t('dashboard.executive.indicators')}
        </h2>

        {/*
          Sem ramo de vazio, e de proposito: a competencia devolve os doze
          escalares ou nao devolve resposta nenhuma. Um cartao que nao tem como
          ficar vazio nao carrega a frase do vazio em lugar nenhum do arquivo — e
          a frase que nao existe e a que nunca aparece na hora errada.
        */}
        {indicators.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
        {!indicators.isError && indicators.data === undefined ? (
          <p className={styles.empty}>{t('app.loading')}</p>
        ) : null}
        {indicators.data ? <Indicators data={indicators.data} me={me} /> : null}
      </section>

      <PanelSection
        id="admin-people"
        title={t('dashboard.admin.peopleTitle')}
        state={roster}
        empty={t('dashboard.admin.peopleEmpty')}
      >
        {(list) => (
          <ul className={styles.list}>
            {list.map((person) => (
              <li key={person.id} className={styles.listItem}>
                {/*
                  `displayName` renderizado como veio. Quem decidiu se este
                  nome sai completo ou mascarado foi o servidor, olhando o papel
                  de quem pediu — a tela nao tem, e nao deve ter, essa decisao.
                */}
                <span>{person.displayName}</span>
                <span className={styles.role}>{t(`role.${person.role}`)}</span>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </>
  );
}

/**
 * O recorte executivo que cabe numa home.
 *
 * Cinco figuras, e nao as doze: a home responde "o mes esta de pe?", e a leitura
 * completa continua no painel executivo, que tem o seletor de competencia e a
 * exportacao. Cada figura vem com a **mesma** definicao adotada do painel, pelo
 * mesmo componente — dois lugares com a mesma palavra e duas definicoes
 * diferentes seria pior do que nao mostrar numero nenhum aqui.
 */
function Indicators({
  data,
  me,
}: {
  readonly data: ExecutiveDashboardModel;
  readonly me: Me;
}) {
  const { t } = useTranslation();
  const count = (value: number): string => formatNumber(value, me.locale);

  return (
    <>
      {/* Exatamente um numero heroi por tela, como no painel completo. */}
      <p className={styles.hero}>
        <span className={styles.tileLabel}>{t('dashboard.executive.recognizedRevenue')}</span>
        <span className={styles.heroValue}>{formatMoney(data.recognizedRevenue, me.locale)}</span>
      </p>
      <Definition text={t('dashboard.executive.definition.recognizedRevenue')} />

      <ul className={styles.tiles}>
        <Tile
          label={t('dashboard.executive.activeStudents')}
          value={count(data.activeStudents)}
          definition={t('dashboard.executive.definition.activeStudents')}
        />
        <Tile
          label={t('dashboard.executive.activeTeachers')}
          value={count(data.activeTeachers)}
          definition={t('dashboard.executive.definition.activeTeachers')}
        />
        <Tile
          label={t('dashboard.executive.scheduledClasses')}
          value={count(data.scheduledClasses)}
          definition={t('dashboard.executive.definition.scheduledClasses')}
        />
        <Tile
          label={t('dashboard.executive.completedClasses')}
          value={count(data.completedClasses)}
          definition={t('dashboard.executive.definition.completedClasses')}
        />
      </ul>
    </>
  );
}
